import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import Box from '@mui/material/Box';

import TwDialog from '../../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../../components/TwButtons/TwButtons';
import TwField, { TwInputTypes } from '../../../../components/TwFields/TwFields';
import TwSelect, { TwSelectOption } from '../../../../components/TwSelects/TwSelects';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';

import './CreateService.scss';
import {
  CreateInvoiceServiceParams,
  getInvoiceServiceUnitLabel,
  InvoiceService,
  InvoiceServiceUnit
} from '../../../../redux/api/invoiceService/invoiceService.model';

import { useAddInvoiceServiceMutation } from '../../../../redux/api/invoiceService/invoiceServiceApi';

import StringUtils from '../../../../utils/stringUtils';
import useAuth from '../../../../providers/Auth/Auth.provider';

export interface CreateServiceProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newService: InvoiceService) => void;
}

interface CreateServiceFormValues {
  serviceName: string;
  unitType: InvoiceServiceUnit;
  defaultRate?: number;
  serviceId: string;
}

const DEFAULT_OPTIONS: TwSelectOption[] = Object.values(InvoiceServiceUnit).map((value) => {
  return {
    label: getInvoiceServiceUnitLabel(value),
    value: value,
    tooltipTitle:
      value === InvoiceServiceUnit.PERCENTAGE
        ? 'Calculated based on the linehaul amount defined under setting'
        : undefined
  };
});

const initialValues: CreateServiceFormValues = {
  serviceName: '',
  unitType: InvoiceServiceUnit.FLAT,
  serviceId: ''
};

const CreateService: React.FC<CreateServiceProps> = ({ open, onClose, onSuccess }): JSX.Element => {
  const { getTenantSetting } = useAuth();
  const [options, setOptions] = useState<TwSelectOption[]>([]);

  const [addInvoiceService] = useAddInvoiceServiceMutation();
  const { addNotification } = useNotifications();
  const showServiceId = getTenantSetting('enable-service-id');

  const { values, handleChange, handleSubmit, resetForm, setFieldValue } = useFormik({
    initialValues: initialValues,
    onSubmit: (values: CreateServiceFormValues): void => {
      const { serviceName, unitType, serviceId } = values;
      if (!serviceName || !unitType || serviceId.length > 25) {
        const messages = {
          serviceName: 'Service name is required',
          unitType: 'Unit type is required',
          serviceId: 'Service ID must be less than 25 characters'
        };

        const message = !serviceName
          ? messages.serviceName
          : !unitType
          ? messages.unitType
          : messages.serviceId;

        addNotification({
          message,
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
        return;
      }

      submitService(values);

      resetForm({
        values: initialValues
      });
    }
  });

  useEffect(() => {
    if (!open) return;
    setOptions(DEFAULT_OPTIONS);
  }, [open]);

  const submitService = (values: CreateServiceFormValues): void => {
    const params: CreateInvoiceServiceParams = {
      name: values.serviceName,
      unit: values.unitType,
      default_rate: values.defaultRate,
      external_service_id: values.serviceId ?? null
    };

    addInvoiceService(params)
      .unwrap()
      .then((payload: InvoiceService) => {
        onSuccess && onSuccess(payload);
        onClose();
        resetForm({
          values: initialValues
        });
      })
      .catch((error: { data: { message: any } }) => {
        addNotification({
          message: error.data.message,
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
      });
  };

  const handleClose = (): void => {
    onClose();
    resetForm({
      values: initialValues
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TwDialog
        open={open}
        onClickClose={handleClose}
        size="sm"
        title="New service"
        actions={[
          <TwButton
            key="1"
            fullwidth={false}
            type={TwButtonType.secondary}
            label="Cancel"
            action={handleClose}
          />,
          <TwButton
            key="2"
            fullwidth={false}
            type={TwButtonType.primary}
            label="Create"
            action={handleSubmit}
          />
        ]}
        sx={{ '& .MuiDialogContent-root': { padding: '35px 20px !important' } }}
      >
        <div className="create-service__wrapper tid-create-service__wrapper">
          <div className="create-service__form-section">
            <div className="create-service__inputs-container">
              <div className="create-service__two-column">
                <div
                  className="tid-create-service__name
                        create-service__two-column-item"
                >
                  <TwField
                    id="serviceName"
                    name="serviceName"
                    value={values.serviceName}
                    label="Service name"
                    inputType={TwInputTypes.text}
                    disabled={false}
                    onInputChange={handleChange}
                    error={false}
                  />
                </div>

                <div
                  className="tid-create-service__type
                        create-service__two-column-item"
                >
                  {showServiceId ? (
                    <TwField
                      id="serviceId"
                      name="serviceId"
                      value={values.serviceId}
                      label="ID (optional)"
                      inputType={TwInputTypes.text}
                      disabled={false}
                      onInputChange={handleChange}
                      error={false}
                    />
                  ) : (
                    <TwSelect
                      id="unitType"
                      name="unitType"
                      label="Unit type"
                      value={values.unitType}
                      optionList={options}
                      onInputChange={handleChange}
                      error={false}
                    />
                  )}
                </div>
              </div>
              <div className="create-service__two-column">
                {showServiceId && (
                  <div
                    className="tid-create-service__rate
                        create-service__two-column-item"
                  >
                    <TwSelect
                      id="unitType"
                      name="unitType"
                      label="Unit type"
                      value={values.unitType}
                      optionList={options}
                      onInputChange={handleChange}
                      error={false}
                    />
                  </div>
                )}
                <div
                  className="tid-create-service__rate
                        create-service__two-column-item"
                >
                  <TwField
                    id="defaultRate"
                    name="defaultRate"
                    value={values.defaultRate}
                    label="Rate (optional)"
                    inputType={TwInputTypes.number}
                    disabled={false}
                    onInputChange={handleChange}
                    error={false}
                    startAdorment={
                      values.unitType !== InvoiceServiceUnit.PERCENTAGE ? '$' : undefined
                    }
                    endAdorment={
                      values.unitType === InvoiceServiceUnit.PERCENTAGE ? '%' : undefined
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </TwDialog>
    </Box>
  );
};

export default CreateService;
